import { useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';

import { COMPLETE_AREA_GRADE, formatGradeValue } from './grade';

const storageKey = (userId: string) => `report-card-last-grade:${userId}`;

// Persisted while the user is genuinely ungraded, so earning the first grade
// while the card is unmounted (e.g. downloading inside the reader, which has
// no sidebar) is still detected as a change when the card next mounts.
const UNGRADED_MARKER = 'none';

// Only the final grade plays the celebratory check.
const PERFECT_LABEL = formatGradeValue(COMPLETE_AREA_GRADE);

export interface GradeChangeReveal {
	/** Play the check reveal: the grade changed to 1,0 and hasn't been seen yet. */
	shouldAnimate: boolean;
	/** Marks the pending change as seen — call when the reveal actually played. */
	onRevealed: () => void;
}

/**
 * Reveal decision for the sidebar grade: the celebratory check plays when the
 * grade reaches 1,0, on a change the user actually witnesses (live, or
 * discovered at mount after earning it elsewhere — the last seen value is
 * persisted per user, including an ungraded marker).
 *
 * A pending reveal is only marked seen via `onRevealed`, once it actually
 * played: the reveal may wait for a clear stage (modal backdrops), and the
 * card can unmount in that window — the next mount then re-detects the change
 * instead of losing the moment. Non-animated values commit immediately.
 *
 * `enabled` must be the card's eligibility: the experiment gate resolves
 * async on every mount, and while the card renders null the hooks still run —
 * without the gate the change would be committed (consumed) off-screen.
 */
export function useGradeChangeReveal(
	label: string | null,
	userId?: string,
	enabled = true
): GradeChangeReveal {
	const reducedMotion = useReducedMotion();
	// Label as of the last committed visible render: `undefined` until then,
	// `null` for a genuinely ungraded user. Distinguishing the two keeps the
	// boot void (user data not loaded yet) from reading as a live change.
	const committedLabelRef = useRef<string | null | undefined>(undefined);
	const storedAtMountRef = useRef<string | null | 'unread'>('unread');

	if (storedAtMountRef.current === 'unread' && userId) {
		storedAtMountRef.current = localStorage.getItem(storageKey(userId));
	}
	const storedAtMount = storedAtMountRef.current === 'unread' ? null : storedAtMountRef.current;
	const committed = committedLabelRef.current;

	const changed =
		committed !== undefined ? committed !== label : storedAtMount !== null && storedAtMount !== label;
	const shouldAnimate = label === PERFECT_LABEL && !!userId && enabled && !reducedMotion && changed;

	useEffect(() => {
		// A pending reveal is committed by onRevealed instead.
		if (!userId || !enabled || shouldAnimate) {
			return;
		}
		committedLabelRef.current = label;
		localStorage.setItem(storageKey(userId), label ?? UNGRADED_MARKER);
	}, [label, userId, enabled, shouldAnimate]);

	const onRevealed = useCallback(() => {
		if (!userId || !label) {
			return;
		}
		committedLabelRef.current = label;
		localStorage.setItem(storageKey(userId), label);
	}, [label, userId]);

	return { shouldAnimate, onRevealed };
}
